import React, { useEffect, useState, useRef } from 'react';
// setApproved is unused while the Action column is removed -- see giftManage below.
import { getScratches } from '../../api/campaign';
import { Dropdown, DropdownItem, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, Modal, ModalBody, ModalHeader, Spinner, Button } from "flowbite-react";
import img from '../../scratch_foreground.jpg';

export default function Dash() {
    const [details, setDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [imageUrl, setImageUrl] = useState('');
    
    // Separate state for each filter
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('all');
    const [selectedWinStatus, setSelectedWinStatus] = useState('all_w');
    const [selectedGiftStatus, setSelectedGiftStatus] = useState('all');
    // Which prize was won: 'all' | 'bag' | 'voucher'
    const [selectedGiftType, setSelectedGiftType] = useState('all');
    const [shopNameSearch, setShopNameSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [shopSuggestions, setShopSuggestions] = useState([]);
    
    const shopInputRef = useRef(null);

    // Get unique shop names from data
    const getUniqueShopNames = () => {
        const shopNames = details.map(item => item.shopName).filter(Boolean);
        return [...new Set(shopNames)].sort();
    };

    // Filter suggestions based on input
    useEffect(() => {
        if (shopNameSearch) {
            const uniqueShops = getUniqueShopNames();
            const filtered = uniqueShops.filter(shop => 
                shop.toLowerCase().includes(shopNameSearch.toLowerCase())
            );
            setShopSuggestions(filtered);
        } else {
            setShopSuggestions(getUniqueShopNames());
        }
    }, [shopNameSearch, details]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (shopInputRef.current && !shopInputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Combined filter function - applies ALL filters together
    const applyAllFilters = () => {
        let filtered = [...details];

        // 1. Apply Date Filter
        if (selectedDate) {
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.dt).toISOString().split('T')[0];
                return itemDate === selectedDate;
            });
        }

        // 2. Apply Brand Filter
        if (selectedBrand === 'inv') {
            filtered = filtered.filter(item => item.innovate?.toLowerCase().includes('x'));
        } else if (selectedBrand === 'atlas') {
            filtered = filtered.filter(item => item.atlas?.toLowerCase().includes('x'));
        }

        // 3. Apply Win/Lost Filter
        if (selectedWinStatus === 'win') {
            filtered = filtered.filter(item => item.s === 1);
        } else if (selectedWinStatus === 'lost') {
            filtered = filtered.filter(item => item.s === 0);
        }

        // 4. Apply Gift Status Filter
        if (selectedGiftStatus === 'g_pen') {
            filtered = filtered.filter(item => 
                item.atlas?.toLowerCase().includes('x') && item.r === 0 && item.s === 1
            );
        } else if (selectedGiftStatus === 'g_done') {
            filtered = filtered.filter(item => 
                item.atlas?.toLowerCase().includes('x') && item.r === 1 && item.s === 1
            );
        }

        // 5. Apply Gift Type Filter (bag / voucher). gift_type is 1 = bag,
        //    2 = voucher, and null on a loss, so both options imply a win.
        if (selectedGiftType === 'bag') {
            filtered = filtered.filter(item => Number(item.gift_type) === 1);
        } else if (selectedGiftType === 'voucher') {
            filtered = filtered.filter(item => Number(item.gift_type) === 2);
        }

        // 6. Apply Shop Name Filter
        if (shopNameSearch) {
            filtered = filtered.filter(item => 
                item.shopName?.toLowerCase().includes(shopNameSearch.toLowerCase())
            );
        }

        setFilteredData(filtered);
    };

    // Re-apply filters whenever any filter changes
    useEffect(() => {
        applyAllFilters();
    }, [details, selectedDate, selectedBrand, selectedWinStatus, selectedGiftStatus, selectedGiftType, shopNameSearch]);

    useEffect(() => {
        getDetails();
    }, []);

    // bill_img is now a direct URL in the file store, so there is nothing to fetch
    const getImage = (billImg) => setImageUrl(billImg || '');

    const getDetails = async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            setDetails(await getScratches());
        } catch (err) {
            console.error("error fetching data: ", err);
            setLoadError('Could not load data. Tap retry.');
        } finally {
            setIsLoading(false);
        }
    };

    // Drove the removed Action column's approve/decline buttons. Kept here (with
    // the setApproved import) so restoring that column is a one-line change.
    // const giftManage = async (id, val) => {
    //     try {
    //         if (val === "approve") {
    //             await setApproved(id, 1);
    //         } else if (val === "decline") {
    //             await setApproved(id, 2);
    //         }
    //         setDetails(prevDetails =>
    //             prevDetails.map(item =>
    //                 item.id === id ? { ...item, a: val === "approve" ? 1 : 2 } : item
    //             )
    //         );
    //     } catch (err) {
    //         console.error("error fetching data: ", err);
    //     }
    // };

    const clearAllFilters = () => {
        setSelectedDate('');
        setSelectedBrand('all');
        setSelectedWinStatus('all_w');
        setSelectedGiftStatus('all');
        setSelectedGiftType('all');
        setShopNameSearch('');
    };

    const handleSuggestionClick = (shopName) => {
        setShopNameSearch(shopName);
        setShowSuggestions(false);
    };

    return (
        <>
            <div className='flex flex-col text-center'>
                <h1 className='py-4 text-3xl font-bold'>Dashboard</h1>

                <div className='px-8 py-4'>
                    <div className='flex gap-4 items-end flex-wrap'>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                Filter by Date
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className="relative" ref={shopInputRef}>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                Filter by Shop Name
                            </label>
                            <input
                                type="text"
                                value={shopNameSearch}
                                onChange={(e) => setShopNameSearch(e.target.value)}
                                onFocus={() => setShowSuggestions(true)}
                                placeholder="Type shop name..."
                                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                            />
                            
                            {showSuggestions && shopSuggestions.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {shopSuggestions.map((shop, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handleSuggestionClick(shop)}
                                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-left text-gray-700 border-b last:border-b-0"
                                        >
                                            {shop}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        {/* CAMPAIGN 2 IS ATLAS ONLY -- brand filter removed, nothing to pick.
                        <Dropdown label={`Brand: ${selectedBrand === 'all' ? 'All' : selectedBrand === 'inv' ? 'Innovate' : 'Atlas'}`}>
                            <DropdownItem onClick={() => setSelectedBrand('all')}>All</DropdownItem>
                            <DropdownItem onClick={() => setSelectedBrand('inv')}>Innovate</DropdownItem>
                            <DropdownItem onClick={() => setSelectedBrand('atlas')}>Atlas</DropdownItem>
                        </Dropdown>
                        */}

                        <Dropdown label={`Status: ${selectedWinStatus === 'all_w' ? 'All' : selectedWinStatus === 'win' ? 'Win' : 'Lost'}`}>
                            <DropdownItem onClick={() => setSelectedWinStatus('all_w')}>All</DropdownItem>
                            <DropdownItem onClick={() => setSelectedWinStatus('win')}>Win</DropdownItem>
                            <DropdownItem onClick={() => setSelectedWinStatus('lost')}>Lost</DropdownItem>
                        </Dropdown>

                        <Dropdown label={`Gift: ${selectedGiftType === 'all' ? 'All' : selectedGiftType === 'bag' ? 'Bag' : 'Voucher'}`}>
                            <DropdownItem onClick={() => setSelectedGiftType('all')}>All</DropdownItem>
                            <DropdownItem onClick={() => setSelectedGiftType('bag')}>Bag</DropdownItem>
                            <DropdownItem onClick={() => setSelectedGiftType('voucher')}>Voucher</DropdownItem>
                        </Dropdown>
                        
                        <Button onClick={clearAllFilters} color="gray">
                            Clear All Filters
                        </Button>
                    </div>
                </div>

                <div className="mb-4 px-8 text-sm text-gray-600 text-left">
                    Showing {filteredData.length} of {details.length} records
                </div>

                {isLoading ? (
                    <div className="py-16 text-center">
                        <Spinner size="xl" />
                        <p className="mt-3 text-sm text-gray-500">Loading data&hellip;</p>
                    </div>
                ) : loadError ? (
                    <div className="py-16 text-center">
                        <p className="mb-3 text-red-600">{loadError}</p>
                        <Button onClick={getDetails}>Retry</Button>
                    </div>
                ) : (
                <div className="overflow-x-auto px-8">
                    <Table>
                        <TableHead>
                            <TableRow>
                                {/* Brand and Action columns removed for campaign 2:
                                    only Atlas runs, and approve/decline is not used. */}
                                <TableHeadCell style={{ width: '5%' }}>#</TableHeadCell>
                                <TableHeadCell style={{ width: '18%' }}>Name</TableHeadCell>
                                <TableHeadCell style={{ width: '13%' }}>Tp</TableHeadCell>
                                <TableHeadCell style={{ width: '24%' }}>Shop Name</TableHeadCell>
                                <TableHeadCell style={{ width: '8%' }}>Win</TableHeadCell>
                                <TableHeadCell style={{ width: '10%' }}>Gift</TableHeadCell>
                                <TableHeadCell style={{ width: '10%' }}>Bill</TableHeadCell>
                                <TableHeadCell style={{ width: '12%' }}>Date &amp; Time</TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="divide-y">
                            {filteredData.map((data, index) => (
                                <TableRow key={index} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                                    <TableCell style={{ width: '5%' }}>{index + 1}</TableCell>
                                    <TableCell style={{ width: '18%' }} className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                                        {data.name}
                                    </TableCell>
                                    <TableCell style={{ width: '13%' }}>{data.tp}</TableCell>
                                    <TableCell style={{ width: '24%' }}>{data.shopName}</TableCell>
                                    <TableCell style={{ width: '8%' }}>{data.s === 0 ? 'Lost' : 'Win'}</TableCell>
                                    <TableCell style={{ width: '10%' }}>{data.giftName || '-'}</TableCell>
                                    <TableCell style={{ width: '10%' }}>
                                        <a href="#" onClick={() => { setOpenModal(true); getImage(data.bill_img); }} className="font-medium text-primary-600 hover:underline dark:text-primary-500">
                                            Bill View
                                        </a>
                                    </TableCell>
                                    <TableCell style={{ width: '12%' }} className="whitespace-nowrap">
                                        {data.dt ? new Date(data.dt).toLocaleString('en-GB', {
                                            day: '2-digit', month: 'short', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: true
                                        }) : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                )}
            </div>
            <>
                <Modal show={openModal} onClose={() => { setOpenModal(false); setImageUrl(null); }}>
                    <ModalHeader>Bill Image</ModalHeader>
                    <ModalBody>
                        {imageUrl ? (
                            <div className='flex justify-center items-center'>
                                <img src={imageUrl} alt="Bill" className="max-h-[70vh] w-auto object-contain" />
                            </div>
                        ) : (
                            <div className="text-center">
                                <Spinner aria-label="Center-aligned Large spinner example" />
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </>
        </>
    );
}